import { customersApi } from "@/core/billing/api/customers-api";
import { creditNotesApi, recurringInvoicesApi } from "@/core/billing/api/documents-api";
import { invoiceTemplatesApi } from "@/core/billing/api/invoice-templates-api";
import { invoicesApi } from "@/core/billing/api/invoices-api";
import { productsApi } from "@/core/billing/api/products-api";
import { paymentsApi } from "@/core/billing/api/payments-api";
import { customerToTableRow } from "@/core/billing/customer-mappers";
import { creditNoteToTableRow, recurringToTableRow } from "@/core/billing/document-mappers";
import { formatMoney, invoiceToTableRow } from "@/core/billing/invoice-mappers";
import { productToTableRow } from "@/core/billing/product-mappers";
import { paymentToTableRow } from "@/core/billing/payment-mappers";
import {
  billingDashboardCharts,
  buildDashboardCharts,
} from "@/core/billing/dashboard-charts";
import type { InvoiceDto } from "@/core/billing/api/invoices-api";

type Row = Record<string, unknown>;
type SelectOption = { value: string; label: string };

const STATUS_LABEL: Record<string, string> = {
  DRAFT: "Draft",
  ISSUED: "Unpaid",
  PAID: "Paid",
  PARTIAL: "Partially Paid",
  OVERDUE: "Overdue",
  CANCELLED: "Cancelled",
  REFUNDED: "Refunded",
};

function replaceRows(target: Row[], rows: Row[]) {
  target.splice(0, target.length, ...rows);
}

function filterByStatusLabels(items: InvoiceDto[], labels: string[]) {
  return items.filter((inv) => labels.includes(STATUS_LABEL[inv.status] ?? inv.status));
}

export const customerReportData: Row[] = [];
export const invoiceAllData: Row[] = [];
export const invoicesPaidData: Row[] = [];
export const InvoiceUnpaidData: Row[] = [];
export const InvoiceOverdueData: Row[] = [];
export const InvoiceDraftData: Row[] = [];
export const InvoiceCancelledData: Row[] = [];
export const invoicePartiallyData: Row[] = [];
export const InvoiceRefundedData: Row[] = [];
export const InvoiceUpcomingData: Row[] = [];
export const productsListData: Row[] = [];
/** Legacy Kanakku export — inventory module moved out of Billing */
export const InventoryListData: Row[] = [];
export const RecurringInvoiceData: Row[] = [];
export const CreditNotesData: Row[] = [];
/** Legacy Kanakku export — quotations admin moved to CRM */
export const quotationsListData: Row[] = [];
export const paymentListData: Row[] = [];
/** Legacy Kanakku exports — finance / procurement modules moved out of Billing */
export const branchesListData: Row[] = [];
export const suppliersListData: Row[] = [];
export const bankaccountListData: Row[] = [];
export const stockTransferListData: Row[] = [];
export const requisitionListData: Row[] = [];
export const purchaseOrdersListData: Row[] = [];
export const purchasesListData: Row[] = [];
export const supplierDebitNotesListData: Row[] = [];
export const supplierPaymentsListData: Row[] = [];

/** Kanakku export name */
export const StockTransferListData = stockTransferListData;
/** Kanakku export name (typo preserved in Kanakku template) */
export const RequistionManagementListData = requisitionListData;
/** Kanakku export names — achats */
export const purchasesOrdersData = purchaseOrdersListData;
export const DebitNotesData = supplierDebitNotesListData;
export const suppliersPaymentListData = supplierPaymentsListData;
/** Kanakku export name */
export const BranchesListData = branchesListData;
/** Kanakku export name */
export const supplierListData = suppliersListData;

export const billingInvoiceSummary = {
  totalAmount: "$0.00",
  paidAmount: "$0.00",
  pendingAmount: "$0.00",
  overdueAmount: "$0.00",
};

export const billingCustName: SelectOption[] = [{ value: "1", label: "Select" }];
export const billingItem: SelectOption[] = [{ value: "1", label: "Select" }];

export const billingProductCatalog = new Map<
  string,
  { name: string; sellingPrice: number; unit: string; taxRate: number }
>();

export { billingDashboardCharts } from "@/core/billing/dashboard-charts";

export const billingDashboardStats = {
  invoiceCount: 0,
  customerCount: 0,
  productCount: 0,
  amountDue: "$0.00",
  totalRevenue: "$0.00",
  paidAmount: "$0.00",
  pendingAmount: "$0.00",
  overdueAmount: "$0.00",
  draftCount: 0,
  paidCount: 0,
  recurringCount: 0,
  creditNotesCount: 0,
  creditNotesTotal: "$0.00",
  paymentsCount: 0,
};

export const billingDashboardCustomers: Row[] = [];
export const billingDashboardInvoices: Row[] = [];
export const billingInvoiceTemplatesData: Row[] = [];

let hydratePromise: Promise<void> | null = null;

export function hydrateBillingKanakkuData(force = false) {
  if (!force && hydratePromise) return hydratePromise;

  hydratePromise = (async () => {
    try {
      const results = await Promise.allSettled([
        customersApi.list({ limit: 500 }),
        invoicesApi.list({ limit: 500 }),
        productsApi.list({ limit: 500 }),
        invoicesApi.summary(),
        recurringInvoicesApi.list({ limit: 500 }),
        creditNotesApi.list({ limit: 500 }),
        invoiceTemplatesApi.list(),
        paymentsApi.list({ limit: 500 }),
      ]);

      const pick = <T,>(index: number, fallback: T): T =>
        results[index].status === "fulfilled"
          ? (results[index] as PromiseFulfilledResult<T>).value
          : fallback;

      const customersRes = pick(0, {
        items: [] as Awaited<ReturnType<typeof customersApi.list>>["items"],
        meta: { total: 0 },
      });
      const invoicesRes = pick(1, { items: [] as InvoiceDto[], meta: { total: 0 } });
      const productsRes = pick(2, {
        items: [] as Awaited<ReturnType<typeof productsApi.list>>["items"],
        meta: { total: 0 },
      });
      const summary = pick(3, {
        totalAmount: "0",
        paidAmount: "0",
        overdueAmount: "0",
        pendingAmount: "0",
        draftCount: 0,
        paidCount: 0,
        totalCount: 0,
      });
      const recurringRes = pick(4, {
        items: [] as Awaited<ReturnType<typeof recurringInvoicesApi.list>>["items"],
        meta: { total: 0 },
      });
      const creditRes = pick(5, {
        items: [] as Awaited<ReturnType<typeof creditNotesApi.list>>["items"],
        meta: { total: 0 },
      });
      const templatesRes = pick(6, { items: [] as Awaited<ReturnType<typeof invoiceTemplatesApi.list>>["items"] });
      const paymentsRes = pick(7, {
        items: [] as Awaited<ReturnType<typeof paymentsApi.list>>["items"],
        meta: { total: 0 },
      });

      for (const [i, r] of results.entries()) {
        if (r.status === "rejected") {
          console.warn("[billing] hydrate partial failure", i, r.reason);
        }
      }

      const invoiceCountByCustomer = new Map<string, number>();
      for (const inv of invoicesRes.items) {
        const cid = inv.customer.id;
        invoiceCountByCustomer.set(cid, (invoiceCountByCustomer.get(cid) ?? 0) + 1);
      }

      replaceRows(
        customerReportData,
        customersRes.items.map((c, i) =>
          customerToTableRow(c, i, invoiceCountByCustomer.get(c.id) ?? 0),
        ),
      );

      const toRows = (items: InvoiceDto[]) =>
        items.map((inv, i) => invoiceToTableRow(inv, i) as Row);

      const all = invoicesRes.items;
      replaceRows(invoiceAllData, toRows(all));
      replaceRows(invoicesPaidData, toRows(filterByStatusLabels(all, ["Paid", "Refunded"])));
      replaceRows(InvoiceUnpaidData, toRows(filterByStatusLabels(all, ["Unpaid"])));
      replaceRows(InvoiceOverdueData, toRows(filterByStatusLabels(all, ["Overdue"])));
      replaceRows(InvoiceDraftData, toRows(filterByStatusLabels(all, ["Draft"])));
      replaceRows(InvoiceCancelledData, toRows(filterByStatusLabels(all, ["Cancelled"])));
      replaceRows(invoicePartiallyData, toRows(filterByStatusLabels(all, ["Partially Paid"])));
      replaceRows(InvoiceRefundedData, toRows(filterByStatusLabels(all, ["Refunded"])));
      replaceRows(InvoiceUpcomingData, []);

      const products = productsRes.items;
      replaceRows(
        productsListData,
        products.map((p, i) => productToTableRow(p, i) as Row),
      );
      replaceRows(
        RecurringInvoiceData,
        recurringRes.items.map((r, i) => recurringToTableRow(r, i) as Row),
      );
      replaceRows(
        CreditNotesData,
        creditRes.items.map((cn, i) => creditNoteToTableRow(cn, i) as Row),
      );
      replaceRows(
        paymentListData,
        paymentsRes.items.map((p, i) => paymentToTableRow(p, i) as Row),
      );

      billingInvoiceSummary.totalAmount = formatMoney(String(summary.totalAmount), "USD");
      billingInvoiceSummary.paidAmount = formatMoney(String(summary.paidAmount), "USD");
      billingInvoiceSummary.overdueAmount = formatMoney(String(summary.overdueAmount), "USD");
      billingInvoiceSummary.pendingAmount = formatMoney(
        String(summary.pendingAmount ?? 0),
        "USD",
      );

      billingDashboardStats.invoiceCount = invoicesRes.meta.total;
      billingDashboardStats.customerCount = customersRes.meta.total;
      billingDashboardStats.productCount = productsRes.meta.total;
      billingDashboardStats.totalRevenue = formatMoney(String(summary.totalAmount), "USD");
      billingDashboardStats.paidAmount = formatMoney(String(summary.paidAmount), "USD");
      billingDashboardStats.pendingAmount = formatMoney(String(summary.pendingAmount ?? 0), "USD");
      billingDashboardStats.overdueAmount = formatMoney(String(summary.overdueAmount), "USD");
      billingDashboardStats.amountDue = billingDashboardStats.pendingAmount;
      billingDashboardStats.draftCount = Number(summary.draftCount ?? 0);
      billingDashboardStats.paidCount = Number(summary.paidCount ?? 0);
      billingDashboardStats.recurringCount = recurringRes.meta.total;
      billingDashboardStats.creditNotesCount = creditRes.meta.total;
      billingDashboardStats.creditNotesTotal = formatMoney(
        String(
          creditRes.items.reduce(
            (sum, cn) => sum + (Number.parseFloat(cn.total) || 0),
            0,
          ),
        ),
        "USD",
      );
      billingDashboardStats.paymentsCount = paymentsRes.meta.total;

      replaceRows(billingDashboardInvoices, toRows(all).slice(0, 5));
      replaceRows(
        billingDashboardCustomers,
        customersRes.items.slice(0, 5).map((c, i) =>
          customerToTableRow(c, i, invoiceCountByCustomer.get(c.id) ?? 0) as Row,
        ),
      );

      const templateImages = [
        "assets/img/invoice/general-invoice-31.svg",
        "assets/img/invoice/general-invoice-32.svg",
        "assets/img/invoice/general-invoice-33.svg",
        "assets/img/invoice/general-invoice-34.svg",
      ];
      const receiptImages = [
        "assets/img/invoice/general-invoice-56.svg",
        "assets/img/invoice/general-invoice-57.svg",
        "assets/img/invoice/general-invoice-58.svg",
        "assets/img/invoice/general-invoice-59.svg",
      ];
      const purchaseImages = [
        "assets/img/invoice/general-invoice-35.svg",
        "assets/img/invoice/general-invoice-36.svg",
        "assets/img/invoice/general-invoice-37.svg",
        "assets/img/invoice/general-invoice-38.svg",
      ];
      replaceRows(
        billingInvoiceTemplatesData,
        templatesRes.items.map((t, i) => ({
          key: t.id,
          id: t.id,
          code: t.code,
          name: t.name,
          category: t.category,
          image:
            t.category === "receipt"
              ? receiptImages[i % receiptImages.length]
              : t.category === "purchase"
                ? purchaseImages[i % purchaseImages.length]
                : templateImages[i % templateImages.length],
        })),
      );

      Object.assign(
        billingDashboardCharts,
        buildDashboardCharts(all, products, summary),
      );

      billingCustName.splice(
        0,
        billingCustName.length,
        { value: "1", label: "Select" },
        ...customersRes.items.map((c) => ({
          value: c.id,
          label: `${c.displayName} (${c.code})`,
        })),
      );

      billingItem.splice(
        0,
        billingItem.length,
        { value: "1", label: "Select" },
        ...products.map((p) => ({
          value: p.id,
          label: p.name,
        })),
      );

      billingProductCatalog.clear();
      for (const p of products) {
        billingProductCatalog.set(p.id, {
          name: p.name,
          sellingPrice: Number.parseFloat(p.sellingPrice) || 0,
          unit: p.unit.symbol,
          taxRate: p.taxRate ? Number.parseFloat(p.taxRate.rate) : 16,
        });
      }
    } finally {
      hydratePromise = null;
    }
  })();

  return hydratePromise;
}
